import { useState } from "react";
import { Link } from "react-router-dom";
import { mainSiteUrl } from "@/mocks/categories";

export default function HeroSection() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value;
    if (honeypot && honeypot.trim() !== "") {
      setSubmitted(true);
      return;
    }
    if (!email.trim()) {
      setError("Please enter your email address.");
      return;
    }
    setSubmitting(true);
    setError("");
    setServerError("");

    try {
      const formData = new FormData(form);
      formData.delete("website_alt");
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });
      const response = await fetch("https://readdy.ai/api/form/d9s7q1me3oq4jqumu6r0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed?.code === "OK") {
        setSubmitted(true);
        setEmail("");
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || "Submission failed. Please try again.";
        setServerError(serverMsg);
      }
    } catch {
      setServerError("Network error. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="relative w-full min-h-[580px] md:min-h-[700px] flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Artistic%20minimal%20flat%20lay%20product%20photography%20with%20scattered%20fashion%20items%20sneakers%20watch%20sunglasses%20fabric%20swatches%20on%20soft%20beige%20textured%20surface%20natural%20sunlight%20casting%20gentle%20shadows%20editorial%20catalog%20aesthetic%20warm%20neutral%20tones%20organized%20chaos%20composition&width=1600&height=900&seq=hero-bg-sugargoo-v2&orientation=landscape"
          alt="Sugargoo Spreadsheet product research concept"
          className="w-full h-full object-cover object-top"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-black/45"></div>

      <div className="relative z-10 w-full px-4 md:px-6 py-20 md:py-28">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-background-50/15 backdrop-blur-sm border border-background-50/20 rounded-full mb-6">
            <i className="ri-sparkling-line text-primary-300 text-sm"></i>
            <span className="text-xs font-medium text-background-50/90 tracking-wide">Updated 2026 Product Lists</span>
          </div>

          <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight mb-5">
            Sugargoo Spreadsheet —<br />
            <span className="text-primary-300">Find Winning Products Faster</span>
          </h2>

          <p className="text-sm md:text-base text-background-200/90 max-w-xl mx-auto mb-8 leading-relaxed">
            The ultimate Sugargoo Spreadsheet resource for discovering products, comparing suppliers,
            saving shipping costs, and building smarter China sourcing strategies.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-10">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="w-full sm:w-auto px-8 py-3.5 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Get Sugargoo Spreadsheet
            </a>
            <Link
              to="/blog/sugargoo-spreadsheet-complete-guide"
              className="w-full sm:w-auto px-8 py-3.5 text-sm font-semibold text-background-50 border border-background-50/30 hover:bg-background-50/10 rounded-lg transition-all duration-200 whitespace-nowrap"
            >
              Explore Complete Guides
            </Link>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
            {["Updated Product Lists", "China Supplier Research", "Shipping Cost Optimization", "Safe Shopping Guides"].map((item) => (
              <div key={item} className="flex items-center gap-1.5">
                <i className="ri-checkbox-circle-fill text-primary-300 text-sm"></i>
                <span className="text-xs text-background-200/80">{item}</span>
              </div>
            ))}
          </div>

          <div className="mt-12 max-w-md mx-auto">
            <p className="text-sm text-background-200/70 mb-3">Get the latest spreadsheet updates &amp; tips</p>
            <form onSubmit={handleSubmit} data-readdy-form="d9s7q1me3oq4jqumu6r0" className="flex flex-col sm:flex-row gap-2">
              <div className="honeypot-wrapper">
                <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              </div>
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                className="flex-1 px-4 py-3 text-sm bg-background-50/15 backdrop-blur-sm border border-background-50/20 rounded-lg text-background-50 placeholder:text-background-300/60 focus:outline-none focus:border-primary-400 transition-colors"
              />
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                {submitting ? "Sending..." : "Subscribe"}
              </button>
            </form>
            {submitted && (
              <p className="text-xs text-primary-300 mt-2">Thanks for subscribing! We&apos;ll keep you updated.</p>
            )}
            {error && (
              <p className="text-xs text-red-400 mt-2">{error}</p>
            )}
            {serverError && (
              <p className="text-xs text-red-400 mt-2">{serverError}</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}